import { useEffect, useMemo, useState } from 'react';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RateReviewIcon from '@mui/icons-material/RateReview';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import {
  Box,
  Breadcrumbs,
  Button,
  Chip,
  Alert,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  Link,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  Divider,
  LinearProgress,
  IconButton,
  DialogActions
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { setFilters, setSort, setPage, setPageSize, fetchCampaigns } from '../store/slices/campaignsSlice';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../api/client';
const statusOptions = [
  { label: 'All Statuses', value: 'ALL' },
  { label: 'DRAFT', value: 'DRAFT' },
  { label: 'PENDING_APPROVAL', value: 'PENDING_APPROVAL' },
  { label: 'REJECTED', value: 'REJECTED' },
  { label: 'SCHEDULED', value: 'SCHEDULED' },
  { label: 'ACTIVE', value: 'ACTIVE' },
  { label: 'PAUSED', value: 'PAUSED' },
  { label: 'COMPLETED', value: 'COMPLETED' },
  { label: 'CANCELLED', value: 'CANCELLED' }
];

const columns = [
  { id: 'name', label: 'Name' },
  { id: 'status', label: 'Status' },
  { id: 'startDate', label: 'Start Date' },
  { id: 'endDate', label: 'End Date' },
  { id: 'priority', label: 'Priority' },
  { id: 'achievedImpressionPct', label: 'Achieved Impression (%)', sortable: false },
  { id: 'achievedImpressionRatio', label: 'Achieved / Total Impression', sortable: false },
  { id: 'actions', label: 'Actions', sortable: false }
];

const formatDate = (value) => {
  if (!value) return '-';
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value;
  }
  const tryDate = new Date(value);
  if (!Number.isNaN(tryDate.getTime())) {
    return tryDate.toLocaleDateString();
  }
  if (typeof value === 'string') {
    const datePart = value.split(' ')[0];
    const fallbackDate = new Date(datePart);
    if (!Number.isNaN(fallbackDate.getTime())) {
      return fallbackDate.toLocaleDateString();
    }
    return datePart || '-';
  }
  return String(value);
};

const formatDateOnlyIso = (value) => {
  if (!value) return '-';
  return formatDate(value);
};

const statusColor = (status) => {
  const upper = (status || '').toUpperCase();
  if (upper === 'ACTIVE') return 'success';
  if (upper === 'SCHEDULED') return 'info';
  if (upper === 'PENDING_APPROVAL') return 'warning';
  if (upper === 'PAUSED') return 'warning';
  if (upper === 'COMPLETED') return 'secondary';
  if (upper === 'CANCELLED' || upper === 'REJECTED') return 'error';
  return 'default'; // includes DRAFT and unknowns
};

const WA_IMAGE_BASE_URL = process.env.REACT_APP_WA_IMAGE_BASE_URL || '/assets/waimages/';
const getWaImageSrc = (path) => {
  if (!path) return '';
  const fileName = path.split(/[/\\]/).pop();
  if (!fileName) return '';
  return `${WA_IMAGE_BASE_URL}${fileName}`;
};

const CampaignListPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [viewCampaign, setViewCampaign] = useState(null);
  const [viewCampaignId, setViewCampaignId] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewError, setViewError] = useState('');
  const [reviewMode, setReviewMode] = useState(false);
  const [statusUpdateLoading, setStatusUpdateLoading] = useState(false);
  const [statusUpdateError, setStatusUpdateError] = useState('');
  const [confirmAction, setConfirmAction] = useState(null); // { id, status, label }
  const { items, filters, sortBy, sortDirection, page, pageSize, meta, error } =
    useSelector((state) => state.campaigns);

  const filteredCampaigns = useMemo(() => {
    const byStatus = (campaign) =>
      filters.status === 'ALL'
        ? true
        : (campaign.status || '').toUpperCase() === (filters.status || '').toUpperCase();

    const byName = (campaign) =>
      campaign.name.toLowerCase().includes((filters.name || '').toLowerCase());

    const byStartDate = (campaign) => {
      if (!filters.startDate) return true;
      return campaign.startDate >= filters.startDate;
    };

    const byEndDate = (campaign) => {
      if (!filters.endDate) return true;
      return campaign.endDate <= filters.endDate;
    };

    const sorted = [...items]
      .filter(byStatus)
      .filter(byName)
      .filter(byStartDate)
      .filter(byEndDate)
      .sort((a, b) => {
        const direction = sortDirection === 'asc' ? 1 : -1;
        if (sortBy === 'priority') {
          const order = ['Low', 'Medium', 'High'];
          return (order.indexOf(a.priority) - order.indexOf(b.priority)) * direction;
        }
        const valueA = a[sortBy] || '';
        const valueB = b[sortBy] || '';
        if (valueA < valueB) return -1 * direction;
        if (valueA > valueB) return 1 * direction;
        return 0;
      });

    return sorted;
  }, [filters, items, sortBy, sortDirection]);

  useEffect(() => {
    dispatch(
      fetchCampaigns({
        page: page + 1,
        pageSize,
        search: filters.name,
        status: filters.status === 'ALL' ? undefined : filters.status,
        startDateFrom: filters.startDate || undefined,
        startDateTo: filters.endDate || undefined,
        sortBy,
        sortDir: sortDirection
      })
    );
  }, [dispatch, page, pageSize, filters, sortBy, sortDirection]);

  // Server-side pagination already applied; use current items as-is.
  const paginatedCampaigns = filteredCampaigns;

  const totalCount = meta?.totalItems ?? filteredCampaigns.length;

  const handleSort = (columnId) => {
    if (columnId === 'actions') return;
    const isAsc = sortBy === columnId && sortDirection === 'asc';
    dispatch(
      setSort({
        sortBy: columnId,
        sortDirection: isAsc ? 'desc' : 'asc'
      })
    );
  };

  const handleFilterChange = (key) => (event) => {
    dispatch(
      setFilters({
        [key]: event.target.value
      })
    );
  };

  const handleView = (campaign) => {
    setViewCampaign(null);
    setViewError('');
    setReviewMode(false);
    setViewCampaignId(campaign.id);
  };

  const handleEdit = (campaign) => {
    navigate('/campaigns/new', { state: { campaign } });
  };

  const handleReview = (campaign) => {
    setViewCampaign(null);
    setViewError('');
    setReviewMode(true);
    setViewCampaignId(campaign.id);
  };

  const refreshList = () => {
    dispatch(
      fetchCampaigns({
        page: page + 1,
        pageSize,
        search: filters.name,
        status: filters.status === 'ALL' ? undefined : filters.status,
        startDateFrom: filters.startDate || undefined,
        startDateTo: filters.endDate || undefined,
        sortBy,
        sortDir: sortDirection
      })
    );
  };

  const handleUpdateStatus = async (nextStatus, targetId) => {
    const target = targetId || viewCampaignId;
    if (!target) return;
    setStatusUpdateLoading(true);
    setStatusUpdateError('');
    try {
      await apiFetch(`/api/campaigns/${target}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: nextStatus })
      });
      if (!targetId) {
        setViewCampaign((prev) => (prev ? { ...prev, status: nextStatus } : prev));
        setReviewMode(false);
        refreshList();
        setViewCampaignId(null);
        setViewCampaign(null);
        setConfirmAction(null);
      } else {
        refreshList();
        setConfirmAction(null);
      }
    } catch (err) {
      setStatusUpdateError(err.message || 'Failed to update status');
    } finally {
      setStatusUpdateLoading(false);
    }
  };

  useEffect(() => {
    const fetchDetail = async () => {
      if (!viewCampaignId) return;
      setViewLoading(true);
      setViewError('');
      try {
        const data = await apiFetch(`/api/campaigns/${viewCampaignId}`);
        setViewCampaign(data.data || null);
      } catch (err) {
        setViewError(err.message || 'Failed to load campaign');
      } finally {
        setViewLoading(false);
      }
    };
    fetchDetail();
  }, [viewCampaignId]);

  return (
    <Stack spacing={3}>
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'flex-start', md: 'center' },
          justifyContent: 'space-between',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2
        }}
      >
        <Box>
          <Breadcrumbs
            aria-label="breadcrumb"
            separator="/"
            sx={{ fontSize: 12, mb: 1, color: 'text.secondary' }}
          >
            <Link underline="hover" color="inherit" href="#">
              Dashboard
            </Link>
            <Link underline="hover" color="inherit" href="#">
              Management
            </Link>
            <Typography color="text.primary">Campaigns</Typography>
          </Breadcrumbs>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Campaigns
          </Typography>
          <Typography color="text.secondary">
            Review, sort, and filter campaigns across the ads platform.
          </Typography>
        </Box>
        <Button
          size="large"
          onClick={() => navigate('/campaigns/new')}
          sx={{
            alignSelf: { xs: 'stretch', md: 'center' },
            width: { xs: '100%', sm: 'auto' },
            maxWidth: { xs: 'calc(100vw - 32px)', sm: 220 },
            boxSizing: 'border-box'
          }}
        >
          + New Campaign
        </Button>
      </Box>

      <Paper
        sx={{
          p: { xs: 2, sm: 3 },
          boxShadow: '0px 24px 48px rgba(15, 23, 42, 0.05)'
        }}
      >
        <Stack spacing={2.5}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            justifyContent="space-between"
            alignItems={{ md: 'center' }}
          >
            <FormControl
              sx={{
                width: { xs: '100%', md: 220 },
                maxWidth: { xs: 'calc(100vw - 32px)', md: 220 },
                boxSizing: 'border-box',
                flexShrink: 0
              }}
            >
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                label="Status"
                onChange={handleFilterChange('status')}
              >
                {statusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 2,
                width: { xs: '100%', md: 'auto' },
                maxWidth: { xs: 'calc(100vw - 32px)', md: '100%' },
                flexGrow: 1,
                boxSizing: 'border-box'
              }}
            >
              <TextField
                value={filters.name}
                onChange={handleFilterChange('name')}
                placeholder="Filter by campaign name"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  )
                }}
                sx={{
                  flexGrow: 1,
                  width: '100%',
                  boxSizing: 'border-box',
                  minWidth: 0
                }}
              />
            </Box>
          </Stack>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              label="Start Date"
              type="date"
              value={filters.startDate}
              onChange={handleFilterChange('startDate')}
              InputLabelProps={{ shrink: true }}
              sx={{ width: { xs: '100%', md: 220 } }}
            />
            <TextField
              label="End Date"
              type="date"
              value={filters.endDate}
              onChange={handleFilterChange('endDate')}
              InputLabelProps={{ shrink: true }}
              sx={{ width: { xs: '100%', md: 220 } }}
            />
          </Stack>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
        {statusUpdateError && !viewCampaignId && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {statusUpdateError}
          </Alert>
        )}

        <Box
          sx={{
            width: '100%',
            overflowX: 'auto',
            border: '1px solid rgba(148, 163, 184, 0.16)',
            borderRadius: 1,
            mt: 3,
            backgroundColor: 'background.paper',
            '&::-webkit-scrollbar': {
              height: 6
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(148, 163, 184, 0.35)',
              borderRadius: 999
            }
          }}
        >
          <TableContainer
            sx={{
              display: 'inline-block',
              minWidth: '100%',
              width: '100%'
            }}
          >
            <Table sx={{ minWidth: 860, borderCollapse: 'separate', borderSpacing: 0 }}>
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      sortDirection={sortBy === column.id ? sortDirection : false}
                    >
                      {column.sortable === false ? (
                        column.label
                      ) : (
                        <TableSortLabel
                          active={sortBy === column.id}
                          direction={sortBy === column.id ? sortDirection : 'asc'}
                          onClick={() => handleSort(column.id)}
                        >
                          {column.label}
                        </TableSortLabel>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedCampaigns.map((campaign) => (
                  <TableRow
                    hover
                    key={campaign.id}
                    sx={{
                      '&:hover': {
                        backgroundColor: 'rgba(15, 23, 42, 0.02)'
                      }
                    }}
                  >
                    <TableCell sx={{ minWidth: 220 }}>
                      <Typography fontWeight={600}>{campaign.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        #{campaign.id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={campaign.status}
                        color={statusColor(campaign.status)}
                        variant="filled"
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{formatDate(campaign.startDate)}</TableCell>
                    <TableCell>{formatDate(campaign.endDate)}</TableCell>
                    <TableCell>
                      <Chip label={campaign.priority} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const achieved = Number(
                          campaign.achievedTotalImpression ??
                            campaign.achieved_total_impression ??
                            0
                        );
                        const total = Number(
                          campaign.totalImpression ?? campaign.total_impression ?? 0
                        );
                        if (!total) return '-';
                        const pct = Math.min(100, Math.round((achieved / total) * 100));
                        return (
                          <Stack spacing={0.5}>
                            <Typography variant="body2">{`${pct}%`}</Typography>
                            <LinearProgress
                              variant="determinate"
                              value={pct}
                              sx={{ height: 6, borderRadius: 999 }}
                            />
                          </Stack>
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const achieved =
                          campaign.achievedTotalImpression ??
                          campaign.achieved_total_impression ??
                          '-';
                        const total =
                          campaign.totalImpression ?? campaign.total_impression ?? '-';
                        return `${achieved ?? '-'} / ${total ?? '-'}`;
                      })()}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        {(campaign.status || '').toUpperCase() === 'PENDING_APPROVAL' ? (
                          <Tooltip title="Review">
                            <IconButton
                              aria-label="review campaign"
                              size="small"
                              onClick={() => handleReview(campaign)}
                            >
                              <RateReviewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="View">
                            <IconButton
                              aria-label="view campaign"
                              size="small"
                              onClick={() => handleView(campaign)}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {(campaign.status || '').toUpperCase() === 'DRAFT' && (
                          <Tooltip title="Edit">
                            <IconButton
                              aria-label="edit campaign"
                              size="small"
                              onClick={() => handleEdit(campaign)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {(campaign.status || '').toUpperCase() === 'ACTIVE' && (
                          <Tooltip title="Pause">
                            <IconButton
                              aria-label="pause campaign"
                              size="small"
                              onClick={() => {
                                setConfirmAction({
                                  id: campaign.id,
                                  status: 'PAUSED',
                                  label: 'Pause this campaign?'
                                });
                              }}
                            >
                              <PauseCircleOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {(campaign.status || '').toUpperCase() === 'PAUSED' && (
                          <>
                            <Tooltip title="Resume">
                              <IconButton
                                aria-label="resume campaign"
                                size="small"
                                onClick={() => {
                                  setConfirmAction({
                                    id: campaign.id,
                                    status: 'SCHEDULED',
                                    label: 'Resume this campaign?'
                                  });
                                }}
                              >
                                <PlayArrowIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Cancel">
                              <IconButton
                                aria-label="cancel campaign"
                                size="small"
                                onClick={() => {
                                  setConfirmAction({
                                    id: campaign.id,
                                    status: 'CANCELLED',
                                    label: 'Cancel this campaign?'
                                  });
                                }}
                              >
                                <StopCircleIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit">
                              <IconButton
                                aria-label="edit campaign"
                                size="small"
                                onClick={() => handleEdit(campaign)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                        {(campaign.status || '').toUpperCase() === 'REJECTED' && (
                          <Tooltip title="Edit">
                            <IconButton
                              aria-label="edit campaign"
                              size="small"
                              onClick={() => handleEdit(campaign)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
                {!paginatedCampaigns.length && (
                  <TableRow>
                    <TableCell colSpan={columns.length}>
                      <Box sx={{ textAlign: 'center', py: 5 }}>
                        <Typography variant="subtitle1" fontWeight={600}>
                          No campaigns found
                        </Typography>
                        <Typography color="text.secondary">
                          Adjust filters to see available campaigns.
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <TablePagination
          component="div"
          count={totalCount}
          rowsPerPage={pageSize}
          rowsPerPageOptions={[5, 10, 25, 50]}
          page={page}
          onPageChange={(_, newPage) => dispatch(setPage(newPage))}
          onRowsPerPageChange={(event) => {
            const nextRows = parseInt(event.target.value, 10);
            dispatch(setPageSize(nextRows));
          }}
          sx={{
            '& .MuiTablePagination-toolbar': {
              px: { xs: 0, sm: 2 }
            }
          }}
        />
      </Paper>
      <Dialog
        open={Boolean(viewCampaignId)}
        onClose={() => {
          setViewCampaignId(null);
          setViewCampaign(null);
          setViewError('');
          setReviewMode(false);
          setStatusUpdateError('');
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Campaign Details
          {viewCampaign?.id ? ` #${viewCampaign.id}` : ''}
        </DialogTitle>
        <DialogContent dividers>
          {viewLoading && (
            <Typography color="text.secondary">Loading...</Typography>
          )}
          {viewError && (
            <Typography color="error" variant="body2">
              {viewError}
            </Typography>
          )}
          {statusUpdateError && (
            <Typography color="error" variant="body2">
              {statusUpdateError}
            </Typography>
          )}
          {viewCampaign && !viewLoading && !viewError && (
            <Stack spacing={3}>
              <Stack spacing={1}>
                <Typography variant="subtitle1" fontWeight={700}>
                  Basic Information
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <Box sx={{ minWidth: 200 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Name
                    </Typography>
                    <Typography fontWeight={600}>{viewCampaign.name}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Status
                    </Typography>
                    <Chip
                      label={viewCampaign.status}
                      size="small"
                      color={statusColor(viewCampaign.status)}
                    />
                  </Box>
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <Box sx={{ minWidth: 200 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Start Date
                    </Typography>
                    <Typography>
                      {formatDateOnlyIso(viewCampaign.startDate || viewCampaign.start_date)}
                    </Typography>
                  </Box>
                  <Box sx={{ minWidth: 200 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      End Date
                    </Typography>
                    <Typography>
                      {formatDateOnlyIso(viewCampaign.endDate || viewCampaign.end_date)}
                    </Typography>
                  </Box>
                  <Box sx={{ minWidth: 200 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Priority
                    </Typography>
                    <Typography>{viewCampaign.priority || '-'}</Typography>
                  </Box>
                </Stack>
                <Typography variant="subtitle2" color="text.secondary">
                  Description
                </Typography>
                <Typography>{viewCampaign.description || '-'}</Typography>
              </Stack>

              <Divider />

              <Stack spacing={1}>
                <Typography variant="subtitle1" fontWeight={700}>
                  Targeting
                </Typography>
                <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Source Channels
                    </Typography>
                  {(viewCampaign.sourceChannels || viewCampaign.source_channels || []).length ? (
                    <Stack direction="row" spacing={0.5} flexWrap="wrap">
                      {(viewCampaign.sourceChannels || viewCampaign.source_channels || []).map((item) => {
                        const label = item?.name || item?.id || item;
                        const key = item?.id || item;
                        return <Chip key={key} label={label} size="small" />;
                      })}
                    </Stack>
                  ) : (
                    <Typography color="text.secondary">-</Typography>
                  )}
                </Box>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <Box sx={{ minWidth: 200 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Origins
                    </Typography>
                    {(viewCampaign.origins || []).length ? (
                      <Stack direction="row" spacing={0.5} flexWrap="wrap">
                        {(viewCampaign.origins || []).map((item) => {
                          const code = item?.code || item;
                          const label = item?.name || item?.alias || code;
                          return <Chip key={code} label={label} size="small" />;
                        })}
                      </Stack>
                    ) : (
                      <Typography color="text.secondary">-</Typography>
                    )}
                  </Box>
                  <Box sx={{ minWidth: 200 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Destinations
                    </Typography>
                    {(viewCampaign.destinations || []).length ? (
                      <Stack direction="row" spacing={0.5} flexWrap="wrap">
                        {(viewCampaign.destinations || []).map((item) => {
                          const code = item?.code || item;
                          const label = item?.name || item?.alias || code;
                          return <Chip key={code} label={label} size="small" />;
                        })}
                      </Stack>
                    ) : (
                      <Typography color="text.secondary">-</Typography>
                    )}
                  </Box>
                </Stack>

                {(viewCampaign.fileUpload ||
                  viewCampaign.file_upload ||
                  viewCampaign.fileName ||
                  viewCampaign.uploadTotalRows) && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      MSISDN Upload
                    </Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                      <Box sx={{ minWidth: 200 }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          File Name
                        </Typography>
                        <Typography>
                          {viewCampaign.fileUpload?.fileName ||
                            viewCampaign.file_upload?.fileName ||
                            viewCampaign.fileUpload?.file_name ||
                            viewCampaign.file_upload?.file_name ||
                            viewCampaign.fileName ||
                            '-'}
                        </Typography>
                      </Box>
                      <Box sx={{ minWidth: 200 }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Total Rows
                        </Typography>
                        <Typography>
                          {viewCampaign.fileUpload?.totalRow ??
                            viewCampaign.file_upload?.totalRow ??
                            viewCampaign.fileUpload?.total_row ??
                            viewCampaign.file_upload?.total_row ??
                            viewCampaign.fileUpload?.totalRows ??
                            viewCampaign.file_upload?.totalRows ??
                            viewCampaign.uploadTotalRows ??
                            '-'}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                )}
              </Stack>

              <Divider />

              <Stack spacing={1}>
                <Typography variant="subtitle1" fontWeight={700}>
                  Channel
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <Box sx={{ minWidth: 200 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Channel
                    </Typography>
                    <Typography>
                      {viewCampaign.deliveryChannel || viewCampaign.channel || '-'}
                    </Typography>
                  </Box>
                  <Box sx={{ minWidth: 200 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Whatsapp Template Id
                    </Typography>
                    <Typography>{viewCampaign.whatsappTemplateId || viewCampaign.whatsapp_template_id || '-'}</Typography>
                  </Box>
                  <Box sx={{ minWidth: 200 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Whatsapp Text
                    </Typography>
                    <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                      {viewCampaign.whatsappText || viewCampaign.whatsapp_text || '-'}
                    </Typography>
                  </Box>
                </Stack>
                {(() => {
                  const src = getWaImageSrc(
                    viewCampaign.whatsappImagePath ||
                      viewCampaign.whatsapp_image_path ||
                      viewCampaign.whatsappHeaderImageFileName ||
                      viewCampaign.whatsapp_header_image_file_name
                  );
                  if (!src) return null;
                  return (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Whatsapp Header Image
                    </Typography>
                    <Box
                      component="img"
                      src={src}
                      alt="WhatsApp Header"
                      sx={{
                        mt: 1,
                        maxWidth: '100%',
                        maxHeight: 220,
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                        objectFit: 'contain',
                        backgroundColor: 'background.paper'
                      }}
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </Box>
                  );
                })()}
              </Stack>

              <Divider />

              <Stack spacing={1}>
                <Typography variant="subtitle1" fontWeight={700}>
                  Frequency Control
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <Box sx={{ minWidth: 200 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Total Impression
                    </Typography>
                    <Typography>
                      {viewCampaign.totalImpression === 0 || viewCampaign.total_impression === 0
                        ? 'Unlimited'
                        : viewCampaign.totalImpression ?? viewCampaign.total_impression ?? '-'}
                    </Typography>
                  </Box>
                  <Box sx={{ minWidth: 200 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Daily Impression
                    </Typography>
                    <Typography>
                      {viewCampaign.dailyImpression === 0 || viewCampaign.daily_impression === 0
                        ? 'Unlimited'
                        : viewCampaign.dailyImpression ?? viewCampaign.daily_impression ?? '-'}
                    </Typography>
                  </Box>
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <Box sx={{ minWidth: 200 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Total Impression per Customer
                    </Typography>
                    <Typography>
                      {viewCampaign.totalImpressionPerCustomer === 0 ||
                      viewCampaign.total_impression_per_customer === 0
                        ? 'Unlimited'
                        : viewCampaign.totalImpressionPerCustomer ??
                          viewCampaign.total_impression_per_customer ??
                          '-'}
                    </Typography>
                  </Box>
                  <Box sx={{ minWidth: 200 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Daily Impression per Customer
                    </Typography>
                    <Typography>
                      {viewCampaign.dailyImpressionPerCustomer === 0 ||
                      viewCampaign.daily_impression_per_customer === 0
                        ? 'Unlimited'
                        : viewCampaign.dailyImpressionPerCustomer ??
                          viewCampaign.daily_impression_per_customer ??
                          '-'}
                    </Typography>
                  </Box>
                </Stack>
              </Stack>

              {Array.isArray(viewCampaign.timePeriods || viewCampaign.time_periods) &&
                (viewCampaign.timePeriods || viewCampaign.time_periods).length > 0 && (
                  <>
                    <Divider />
                    <Stack spacing={1}>
                      <Typography variant="subtitle1" fontWeight={700}>
                        Day and time
                      </Typography>
                      {(viewCampaign.timePeriods || viewCampaign.time_periods || []).map((period, idx) => (
                        <Box
                          key={`${period.startTime || period.start_time}-${idx}`}
                          sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            p: 2
                          }}
                        >
                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <Typography>
                              <strong>Start:</strong> {period.startTime || period.start_time || '-'}
                            </Typography>
                            <Typography>
                              <strong>End:</strong> {period.endTime || period.end_time || '-'}
                            </Typography>
                          </Stack>
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                              Repeating on
                            </Typography>
                            {period.days?.length ? (
                              <Stack direction="row" spacing={0.5} flexWrap="wrap">
                                {period.days.map((day) => (
                                  <Chip key={day} label={day} size="small" />
                                ))}
                              </Stack>
                            ) : (
                              <Typography color="text.secondary">-</Typography>
                            )}
                          </Box>
                        </Box>
                      ))}
                    </Stack>
                  </>
                )}

              {(reviewMode ||
                ((viewCampaign?.status || '').toUpperCase() === 'PENDING_APPROVAL')) && (
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <Button
                    variant="outlined"
                    color="error"
                    disabled={statusUpdateLoading}
                    onClick={() => handleUpdateStatus('REJECTED')}
                  >
                    Reject
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={statusUpdateLoading}
                    onClick={() => handleUpdateStatus('SCHEDULED')}
                  >
                    Approve
                  </Button>
                </Stack>
              )}
            </Stack>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(confirmAction)}
        maxWidth="xs"
        fullWidth
        onClose={() => {
          setConfirmAction(null);
          setStatusUpdateError('');
        }}
      >
        <DialogTitle>Confirm</DialogTitle>
        <DialogContent>
          <Typography>{confirmAction?.label || 'Are you sure?'}</Typography>
          {statusUpdateError && (
            <Typography color="error" variant="body2" sx={{ mt: 1 }}>
              {statusUpdateError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmAction(null)}
            disabled={statusUpdateLoading}
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => handleUpdateStatus(confirmAction?.status, confirmAction?.id)}
            disabled={statusUpdateLoading}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default CampaignListPage;
